import { Link } from 'react-router-dom';

const services = [
  {
    icon: 'ri-mental-health-line',
    title: 'Thyroid & Adrenal Solutions',
    description: 'Comprehensive hormone balancing for thyroid disorders and adrenal fatigue. Advanced testing uncovers root causes, not just symptoms. Personalized protocols restore energy, metabolism, and vitality in Bergen County patients who\'ve been told "everything looks normal."',
  },
  {
    icon: 'ri-capsule-line',
    title: 'Bio-Identical Hormone Replacement',
    description: 'Natural, bioidentical hormone therapy tailored to your unique biochemistry. Addresses menopause, andropause, low libido, fatigue, and weight gain using hormones that work with your body — not against it.',
  },
  {
    icon: 'ri-test-tube-line',
    title: 'Chelation & IV Therapy',
    description: 'Intravenous EDTA chelation removes heavy metals (lead, mercury, arsenic) and reduces atherosclerotic plaque. High-dose vitamin C infusions and customized nutrient IVs offer powerful healing that oral supplements cannot match.',
  },
  {
    icon: 'ri-brain-line',
    title: 'Alternatives to Psychiatric Medications',
    description: 'Orthomolecular psychiatry identifies biochemical imbalances driving anxiety, depression, and mood disorders. Nutritional supplementation and lifestyle medicine can dramatically reduce or eliminate the need for pharmaceutical drugs.',
  },
  {
    icon: 'ri-moon-line',
    title: 'Sleep Solutions',
    description: 'Chronic sleep disorders are often rooted in hormonal imbalances, nutritional deficiencies, and nervous system dysregulation. Dr. Leder investigates underlying causes to restore healthy, restorative sleep naturally.',
  },
  {
    icon: 'ri-heart-add-line',
    title: 'Weight Loss & Metabolic Programs',
    description: 'Personalized metabolic programs including HCG protocols address the root causes of stubborn weight gain — hormonal, inflammatory, and nutritional — rather than offering generic calorie-cutting advice.',
  },
  {
    icon: 'ri-microscope-line',
    title: 'Specialized Functional Testing',
    description: 'Advanced diagnostic panels — comprehensive hormone testing, food sensitivity analysis, heavy metal screening, gut microbiome assessment, and nutritional evaluations — reveal what standard labs miss entirely.',
  },
  {
    icon: 'ri-heart-pulse-line',
    title: 'Total Health Makeover',
    description: 'A comprehensive whole-body transformation program. Includes full-system evaluation, personalized protocols, nutritional guidance, and long-term support to rebuild your health from the foundation up.',
  },
  {
    icon: 'ri-parent-line',
    title: 'Biomedical Protocols for Spectrum Disorders',
    description: 'Specialized biomedical approaches for autism spectrum and developmental challenges. Comprehensive testing and individualized treatment protocols support optimal neurological function and quality of life.',
  },
  {
    icon: 'ri-user-heart-line',
    title: "Women's & Men's Wellness",
    description: 'Gender-specific health optimization addressing hormonal health, sexual wellness, fertility, perimenopause, menopause, andropause, and age-related concerns through natural, evidence-informed solutions.',
  },
  {
    icon: 'ri-heart-line',
    title: 'Couples & Family Counseling',
    description: "Dr. Leder's background in psychology and orthomolecular psychiatry extends to compassionate counseling for couples and families navigating relationship challenges, communication issues, and life transitions.",
  },
  {
    icon: 'ri-medicine-bottle-line',
    title: 'Medical Marijuana Certification',
    description: 'Comprehensive evaluation and state-compliant certification for medical cannabis use in New Jersey. Dr. Leder assesses whether medical marijuana is an appropriate part of your individualized treatment plan.',
  },
];

interface ServicesGridProps {
  compact?: boolean;
}

export default function ServicesGrid({ compact = false }: ServicesGridProps) {
  const displayed = compact ? services.slice(0, 6) : services;

  return (
    <section className="py-20 px-6 lg:px-12 bg-white">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-14">
          <p className="text-teal-600 text-sm font-semibold tracking-widest uppercase mb-3">Comprehensive Programs</p>
          <h2 className="font-heading text-4xl lg:text-5xl font-bold text-teal-950 mb-5">
            What We Treat & How We Help
          </h2>
          <p className="text-gray-600 text-lg max-w-3xl mx-auto leading-relaxed">
            Every program at A Better Alternative Medical Center is built around root-cause investigation, not symptom suppression. Dr. Leder's 35+ years of functional medicine experience means you get answers that actually change your life.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayed.map((service, i) => (
            <div
              key={i}
              className="group bg-white border border-teal-100 rounded-lg p-7 hover:border-teal-400 hover:bg-teal-50 transition-all duration-200 cursor-default"
            >
              <div className="w-12 h-12 flex items-center justify-center bg-teal-100 group-hover:bg-teal-200 rounded-md mb-5 transition-colors">
                <i className={`${service.icon} text-teal-700 text-2xl`}></i>
              </div>
              <h3 className="font-heading text-lg font-semibold text-teal-950 mb-3">{service.title}</h3>
              <p className="text-gray-600 text-sm leading-relaxed">{service.description}</p>
            </div>
          ))}
        </div>

        {compact && (
          <div className="text-center mt-12">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 bg-teal-700 text-white px-8 py-3.5 rounded-md text-sm font-semibold hover:bg-teal-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              View All 12 Programs
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-white text-base"></i>
              </div>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
